import type { DynamicUICommon } from "~/modules/dtwin/models/DynamicUICommon.interface";

export function mergeUIConfig<T extends DynamicUICommon>(
  genericUIConfig: T[],
  modelUIConfig: T[],
) {
  const mergedUIConfig = [...genericUIConfig];
  const groupedAfterUIConfig: Record<string, T[]> = groupBy(
    modelUIConfig,
    "afterElementId",
  );
  const groupedBeforeUIConfig: Record<string, T[]> = groupBy(
    modelUIConfig,
    "beforeElementId",
  );
  for (const item of modelUIConfig) {
    if (item.hide) {
      const index = mergedUIConfig.findIndex(({ id }) => id === item.id);
      if (index > -1) {
        mergedUIConfig.splice(index, 1);
      }
    } else if (item.afterElementId) {
      insertElementBeforeOrAfter(
        item,
        mergedUIConfig,
        groupedAfterUIConfig,
        "afterElementId",
      );
    } else if (item.beforeElementId) {
      insertElementBeforeOrAfter(
        item,
        mergedUIConfig,
        groupedBeforeUIConfig,
        "beforeElementId",
      );
    } else {
      const index = mergedUIConfig.findIndex(({ id }) => id === item.id);
      if (index > -1) {
        mergedUIConfig[index] = {
          ...mergedUIConfig[index],
          ...item,
        };
      } else {
        mergedUIConfig.push(item);
      }
    }
  }
  return mergedUIConfig;
}

/**
 * Group an array of objects by a given key
 * @param array
 * @param key
 */
export function groupBy<T>(array: T[], key: string): Record<string, T[]> {
  return array.reduce((accumulator: Record<string, T[]>, currentValue: T) => {
    const groupKey = String(currentValue[key as keyof T]);
    if (!accumulator[groupKey]) {
      accumulator[groupKey] = [];
    }
    accumulator[groupKey].push(currentValue);
    return accumulator;
  }, {});
}

/**
 * Function to insert an element before or after another element in the generic UI config
 * @param item the item to insert
 * @param mergedUIConfig the current merged UI config
 * @param groupedUIConfig the UI config grouped by "propertyReference"
 * @param propertyReference the property used to group element
 */
function insertElementBeforeOrAfter(
  item: DynamicUICommon,
  mergedUIConfig: DynamicUICommon[],
  groupedUIConfig: Record<string, DynamicUICommon[]>,
  propertyReference: "afterElementId" | "beforeElementId",
): DynamicUICommon[] {
  const referenceId = item[propertyReference];
  // To avoid duplicates we need to check if the item is already in the mergedUIConfig
  const occurences = mergedUIConfig.filter((element) => element.id === item.id);
  if (occurences.length <= 1) {
    const index = mergedUIConfig.findIndex(({ id }) => id === referenceId);
    if (index > -1 && referenceId && groupedUIConfig[referenceId]) {
      const insertIndex =
        propertyReference === "afterElementId" ? index + 1 : index;
      mergedUIConfig.splice(insertIndex, 0, ...groupedUIConfig[referenceId]);
      delete groupedUIConfig[referenceId];
    }
  }
  if (occurences.length >= 1) {
    // Duplicates could happen if the item is in the genericUIConfig and the modelUIConfig later via groupedUIConfig
    // we need to remove it
    const indexGeneric = mergedUIConfig.findIndex(
      (element) => element.id === item.id && !element[propertyReference],
    );
    const indexModel = mergedUIConfig.findIndex(
      (element) => element.id === item.id && element[propertyReference],
    );
    if (indexGeneric > -1) {
      if (indexModel > -1) {
        // we merge the duplicates because one come from genericUiConfig and the other from modelUIConfig
        mergedUIConfig[indexModel] = {
          ...mergedUIConfig[indexGeneric],
          ...mergedUIConfig[indexModel],
        };
      }
      mergedUIConfig.splice(indexGeneric, 1);
    }
  }
  return mergedUIConfig;
}
